import { useAuth0 } from '@auth0/auth0-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { Monitor, DeviceMobileCamera, CaretLeft } from '@phosphor-icons/react'
import { useRecoilValue } from 'recoil'
import { z } from 'zod'
import { UpdateCustomerSettingsBodySchema } from '../../../../server/src/customer/customer.contract'
import { useCustomerClient } from '@/lib/api'
import { Button } from '@/components/button'
import { tokenState } from '@/store/atoms/token'
import { uploadFileGeneric } from '@/utils/file-upload-helper'
import { ValidatePage } from '../../../../server/src/gift-portal/views/default/validate-page'
import { SelectPage } from '../../../../server/src/gift-portal/views/default/select-page'
import { SuccessPage } from '../../../../server/src/gift-portal/views/default/success-page'
import { EmailPreview } from '../../../../server/src/gift-portal/views/default/email-preview'
import {
  ThemeEditorFormSchema,
  type PortalTheme,
  type ThemeEditorFormValues,
} from '@/schema/gift-portal.schema'
import { getTranslations } from '../../../../server/src/gift-portal/i18n'
import type { PortalTranslations } from '../../../../server/src/gift-portal/i18n'
import { cn } from '@alphabait/ui/lib/utils'

const DEFAULT_COLORS = {
  primary: '#1a1a2e',
  background: '#ffffff',
  foreground: '#1a1a2e',
  cardBackground: '#ffffff',
  cardForeground: '#666666',
  button: '#1a1a2e',
}

const COLOR_LABELS: Record<string, string> = {
  primary: 'Primary colour',
  foreground: 'Text colour',
  cardForeground: 'Card text colour',
  background: 'Page background',
  cardBackground: 'Card background',
  button: 'Button colour',
}

interface FontFileEntry {
  key: string
  weight: string
}

interface PendingFontFile {
  file: File
  weight: string
}

type UpdateSettingsBody = z.infer<typeof UpdateCustomerSettingsBodySchema>

export default function ThemeEditorPage() {
  const { customerId } = useParams<{ customerId: string }>()
  const queryClient = useQueryClient()
  const customerClient = useCustomerClient()
  const idToken = useRecoilValue(tokenState)
  const token = idToken?.__raw || ''

  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [bgFile, setBgFile] = useState<File | null>(null)
  const [savedFontFiles, setSavedFontFiles] = useState<FontFileEntry[]>([])
  const [pendingFontFiles, setPendingFontFiles] = useState<PendingFontFile[]>(
    [],
  )
  const newFontWeight = '400'
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [previewTab, setPreviewTab] = useState<
    'access' | 'select' | 'success' | 'email'
  >('access')
  const [previewLang, setPreviewLang] = useState<string>('nl')
  const [editorTab, setEditorTab] = useState<'details' | 'theming' | 'content'>(
    'details',
  )
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>(
    'desktop',
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<ThemeEditorFormValues>({
    resolver: zodResolver(ThemeEditorFormSchema),
    defaultValues: {
      slug: '',
      portalColors: { ...DEFAULT_COLORS },
      fontFamily: 'system-ui, -apple-system, sans-serif',
      logoUrl: '',
      backgroundUrl: '',
      validityDays: null,
    },
  })

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['customerSettings', customerId],
    queryFn: async () => {
      const response = await customerClient.settings.query({
        params: { customerId: customerId! },
      })
      return response.status === 200 ? response.body : null
    },
    enabled: !!customerId,
  })

  useEffect(() => {
    if (settingsResponse) {
      reset({
        slug: settingsResponse.slug || '',
        portalColors: {
          primary:
            settingsResponse.portalColors?.primary || DEFAULT_COLORS.primary,
          background:
            settingsResponse.portalColors?.background ||
            DEFAULT_COLORS.background,
          foreground:
            settingsResponse.portalColors?.foreground ||
            DEFAULT_COLORS.foreground,
          cardBackground:
            settingsResponse.portalColors?.cardBackground ||
            DEFAULT_COLORS.cardBackground,
          cardForeground:
            settingsResponse.portalColors?.cardForeground ||
            DEFAULT_COLORS.cardForeground,
          button:
            settingsResponse.portalColors?.button || DEFAULT_COLORS.button,
        },
        fontFamily:
          settingsResponse.fontFamily || 'system-ui, -apple-system, sans-serif',
        logoUrl: settingsResponse.logoUrl || '',
        backgroundUrl: settingsResponse.backgroundUrl || '',
        validityDays: settingsResponse.validityDays ?? null,
      })
      setSavedFontFiles(settingsResponse.fontFiles || [])
    }
  }, [settingsResponse, reset])

  const updateMutation = useMutation({
    mutationFn: async (body: UpdateSettingsBody) => {
      const response = await customerClient.updateSettings.mutate({
        params: { customerId: customerId! },
        body,
      })
      if (response.status !== 200) {
        const message =
          (response.body as { message?: string })?.message ||
          'Failed to save settings'
        if (response.status === 409) {
          setError('slug', { type: 'server', message })
        }
        throw new Error(message)
      }
      return response.body
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customerSettings', customerId],
      })
      setSaveMessage('Settings saved')
      setTimeout(() => setSaveMessage(''), 3000)
    },
  })

  const uploadAsset = async (
    file: File,
    assetType: 'logo' | 'background' | 'font',
  ): Promise<string> => {
    const responseText = await uploadFileGeneric({
      file,
      uploadUrl: '/upload/customer-theme-asset',
      uploadBody: { customerId: customerId!, assetType },
      token,
    })
    const parsed = JSON.parse(responseText)
    return parsed.key
  }

  const onInvalid = (formErrors: typeof errors) => {
    const first = Object.values(formErrors)[0] as
      | { message?: string }
      | undefined
    setSaveMessage(first?.message || 'Please fix the highlighted fields')
    if (formErrors.slug) {
      setEditorTab('details')
    }
  }

  const onSubmit = async (values: ThemeEditorFormValues) => {
    setSaving(true)
    setSaveMessage('')
    try {
      let logoUrl = values.logoUrl
      let backgroundUrl = values.backgroundUrl

      if (logoFile) {
        logoUrl = await uploadAsset(logoFile, 'logo')
      }
      if (bgFile) {
        backgroundUrl = await uploadAsset(bgFile, 'background')
      }

      // Upload pending font files
      const uploadedFonts = await Promise.all(
        pendingFontFiles.map(async (pf) => ({
          key: await uploadAsset(pf.file, 'font'),
          weight: pf.weight,
        })),
      )
      const allFontFiles = [...savedFontFiles, ...uploadedFonts]

      // Filter out colors that match defaults and validate hex format
      const portalColors: Record<string, string> = {}
      const validHex = /^#[0-9A-Fa-f]{6}$/
      for (const [key, value] of Object.entries(values.portalColors)) {
        if (
          value &&
          validHex.test(value) &&
          value !== DEFAULT_COLORS[key as keyof typeof DEFAULT_COLORS]
        ) {
          portalColors[key] = value
        }
      }

      await updateMutation.mutateAsync({
        portalColors:
          Object.keys(portalColors).length > 0 ? portalColors : null,
        fontFamily: allFontFiles.length > 0 ? null : values.fontFamily || null,
        fontFiles: allFontFiles.length > 0 ? allFontFiles : null,
        logoUrl: logoUrl || null,
        backgroundUrl: backgroundUrl || null,
        validityDays: values.validityDays,
        slug: values.slug,
      })

      queryClient.invalidateQueries({
        queryKey: ['customerSettings', customerId],
      })
      setLogoFile(null)
      setBgFile(null)
      setPendingFontFiles([])
      setSaveMessage('Settings saved')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err: unknown) {
      setSaveMessage(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const currentColors = watch('portalColors')
  const currentFont = watch('fontFamily')
  const currentLogoUrl = watch('logoUrl')
  const currentBgUrl = watch('backgroundUrl')

  const hasFontOverride =
    savedFontFiles.length > 0 || pendingFontFiles.length > 0

  const previewFontFiles = [
    ...savedFontFiles.map((f) => ({
      url: `/s3-files/${f.key}`,
      weight: f.weight,
    })),
    ...pendingFontFiles.map((f) => ({
      url: URL.createObjectURL(f.file),
      weight: f.weight,
    })),
  ]

  const previewTheme: PortalTheme = {
    colors: currentColors,
    logoUrl: logoFile
      ? URL.createObjectURL(logoFile)
      : currentLogoUrl
      ? `/s3-files/${currentLogoUrl}`
      : null,
    backgroundUrl: bgFile
      ? URL.createObjectURL(bgFile)
      : currentBgUrl
      ? `/s3-files/${currentBgUrl}`
      : null,
    fontFiles: previewFontFiles,
    fontFamily: hasFontOverride ? '"PortalFont", sans-serif' : currentFont,
  }

  if (isLoading) return <div className="p-6">Loading...</div>

  return (
    <div className="py-6 px-4">
      {/* Back button */}
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-2 h-8 text-sm text-foreground  rounded-full hover:bg-gray-800/5 mb-2"
      >
        <CaretLeft weight="bold" className="w-4 h-4" />
        Customers
      </Link>

      {/* Header row */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="!text-2xl !font-semibold leading-7 text-foreground !mb-0">
            Gift Portal
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage your profile details such as name, avatar, email and bio.
          </p>
        </div>
        <Button
          type="button"
          primary
          size="small"
          label={saving ? 'Saving...' : 'Save'}
          disabled={saving}
          onClick={handleSubmit(onSubmit, onInvalid)}
          className="bg-accent rounded-full"
        />
      </div>

      {saveMessage && (
        <div
          className={`text-sm mb-4 ${
            saveMessage === 'Settings saved' ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {saveMessage}
        </div>
      )}

      {/* Two-column layout - left scrollable, right fixed */}
      <div className="flex gap-6 h-[calc(100vh-160px)]">
        {/* Left column - Form controls (scrollable) */}
        <div className="w-[430px] shrink-0 overflow-y-auto">
          {/* Toggle group - connected pill buttons */}
          <div className="flex items-center w-full">
            {(['details', 'theming', 'content'] as const).map((tab, i) => (
              <button
                key={tab}
                type="button"
                onClick={() => setEditorTab(tab)}
                className={`flex-1 px-3 py-2 text-sm font-medium border border-base-input cursor-pointer shadow-xs ${
                  i === 0 ? 'rounded-l-full' : i === 2 ? 'rounded-r-full' : ''
                } ${i > 0 ? '-ml-px' : ''} ${
                  editorTab === tab
                    ? 'bg-base-muted text-foreground'
                    : 'bg-white text-foreground'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            {editorTab === 'details' && (
              <div className="flex flex-col gap-4 px-2 mt-6">
                {/* URL slug */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    URL slug
                  </label>
                  <div className="flex items-center gap-2 h-9 bg-white border border-base-input rounded-lg shadow-xs px-3 py-1">
                    <span className="text-sm text-foreground whitespace-nowrap">
                      alphabait.gift
                    </span>
                    <span className="text-sm text-muted-foreground">/</span>
                    <Controller
                      name="slug"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="text"
                          value={field.value}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9-]/g, ''),
                            )
                          }
                          className="flex-1 min-w-0 text-sm text-foreground bg-transparent border-none outline-none"
                          placeholder="name-of-product"
                        />
                      )}
                    />
                  </div>
                  {errors.slug?.message && (
                    <span className="text-xs text-red-600">
                      {errors.slug.message}
                    </span>
                  )}
                </div>

                {/* Validity */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Validity
                  </label>
                  <Controller
                    name="validityDays"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2 h-9 bg-white border border-base-input rounded-lg shadow-xs px-3 py-1">
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const v = e.target.value
                            field.onChange(
                              v === ''
                                ? null
                                : Math.max(1, parseInt(v, 10) || 1),
                            )
                          }}
                          placeholder="No expiration"
                          className="flex-1 min-w-0 text-sm text-foreground bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          days
                        </span>
                      </div>
                    )}
                  />
                  <p className="text-xs text-muted-foreground leading-4">
                    How long the gift portal link stays active, counted from the
                    moment the gift email is sent — not from the campaign start
                    or end date. Each recipient gets the full window from their
                    own send date: a link sent on the last day of the campaign
                    stays active after the campaign ends, and a link sent early
                    with a short validity can expire while the campaign is still
                    running. The value is snapshotted onto each link at send
                    time, so changing it here does not affect already-sent
                    links. Leave empty for "never expires".
                  </p>
                </div>
              </div>
            )}

            {editorTab === 'theming' && (
              <p className="text-sm font-light leading-5 text-muted-foreground mt-10 mb-10">
                Theming is applied on all pages of the user facing screens
                (access, select gift, success, email)
              </p>
            )}

            {editorTab === 'theming' && (
              <div className="flex flex-col gap-10">
                {/* Colour Palette */}
                <section className="px-2">
                  <h3 className="text-sm font-medium leading-none text-foreground mb-4">
                    Colour Palette
                  </h3>
                  <div className="flex flex-col gap-4">
                    {(
                      Object.keys(
                        COLOR_LABELS,
                      ) as (keyof typeof DEFAULT_COLORS)[]
                    ).map((key) => (
                      <Controller
                        key={key}
                        name={`portalColors.${key}`}
                        control={control}
                        render={({ field }) => {
                          const value =
                            (field.value as string) || DEFAULT_COLORS[key]
                          return (
                            <div className="flex flex-col gap-[7px]">
                              <label className="text-xs leading-none text-foreground">
                                {COLOR_LABELS[key]}
                              </label>
                              <div className="flex items-center gap-[7px]">
                                <input
                                  type="color"
                                  value={value}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  className="w-9 h-9 rounded-lg border border-base-input cursor-pointer p-0 shrink-0 appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch]:rounded-[7px] [&::-moz-color-swatch]:border-none [&::-moz-color-swatch]:rounded-[7px]"
                                />
                                <div className="flex-1 flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1 gap-2">
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => {
                                      const v = e.target.value
                                      if (/^#[0-9A-Fa-f]{0,6}$/.test(v))
                                        field.onChange(v)
                                    }}
                                    className="flex-1 text-sm text-muted-foreground bg-transparent border-none outline-none min-w-0"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      field.onChange(DEFAULT_COLORS[key])
                                    }
                                    disabled={value === DEFAULT_COLORS[key]}
                                    className={`h-6 px-2 rounded-[3px] text-sm font-medium shrink-0 ${
                                      value !== DEFAULT_COLORS[key]
                                        ? 'bg-base-muted text-foreground cursor-pointer hover:bg-gray-500/20'
                                        : 'text-muted-foreground opacity-0'
                                    }`}
                                  >
                                    reset
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        }}
                      />
                    ))}
                  </div>
                </section>

                {/* Background Image */}
                <section className="px-2">
                  <h3 className="text-sm font-medium leading-none text-foreground mb-4">
                    Background image
                  </h3>
                  <Controller
                    name="backgroundUrl"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-[7px]">
                        {(field.value || bgFile) && (
                          <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
                            <img
                              src={
                                bgFile
                                  ? URL.createObjectURL(bgFile)
                                  : `/s3-files/${field.value}`
                              }
                              alt="Background"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1 gap-2">
                          <span
                            className={`flex-1 min-w-0 text-sm truncate ${
                              bgFile || field.value
                                ? 'text-foreground'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {bgFile
                              ? bgFile.name
                              : field.value
                              ? field.value.split('/').pop()
                              : 'No file'}
                          </span>
                          {(field.value || bgFile) && (
                            <button
                              type="button"
                              onClick={() => {
                                field.onChange('')
                                setBgFile(null)
                              }}
                              className="bg-muted w-6 h-6 flex items-center justify-center rounded-[2px] shrink-0 cursor-pointer hover:bg-gray-500/20 text-xs"
                            >
                              🗑
                            </button>
                          )}
                          <label className="bg-muted h-6 px-2 rounded-[3px] text-sm font-base-muted text-foreground shrink-0 cursor-pointer hover:bg-gray-500/20 flex items-center">
                            change
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) setBgFile(file)
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  />
                </section>

                {/* Font */}
                <section className="px-2">
                  <h3 className="text-sm font-medium leading-none text-foreground mb-4">
                    Font
                  </h3>
                  <div className="flex flex-col gap-4">
                    {/* File upload input */}
                    <div>
                      <div className="flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs px-3 py-1 gap-1 overflow-hidden">
                        <label className="text-sm font-medium text-foreground px-1.5 py-px shrink-0 cursor-pointer m-auto">
                          Choose file
                          <input
                            type="file"
                            accept=".woff,.woff2,.ttf,.otf"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                setPendingFontFiles((prev) => [
                                  ...prev,
                                  { file, weight: newFontWeight },
                                ])
                                e.target.value = ''
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground leading-none mt-2">
                        Upload your own font for better branding experience
                      </p>
                    </div>

                    {/* Existing font files */}
                    {savedFontFiles.map((f, i) => (
                      <div
                        key={`saved-${i}`}
                        className="flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1 gap-2"
                      >
                        <span className="flex-1 text-sm text-primary truncate">
                          {f.key.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setSavedFontFiles((prev) =>
                              prev.filter((_, j) => j !== i),
                            )
                          }
                          className="bg-muted h-6 px-2 rounded-[3px] text-sm font-medium text-foreground shrink-0 cursor-pointer hover:bg-gray-500/20"
                        >
                          change
                        </button>
                      </div>
                    ))}
                    {pendingFontFiles.map((f, i) => (
                      <div
                        key={`pending-${i}`}
                        className="flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1 gap-2"
                      >
                        <span className="flex-1 text-sm text-primary truncate">
                          {f.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingFontFiles((prev) =>
                              prev.filter((_, j) => j !== i),
                            )
                          }
                          className="bg-muted h-6 px-2 rounded-[3px] text-sm font-medium text-foreground shrink-0 cursor-pointer hover:bg-gray-500/20"
                        >
                          change
                        </button>
                      </div>
                    ))}

                    {/* Font family fallback */}
                    {!hasFontOverride && (
                      <Controller
                        name="fontFamily"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1">
                            <input
                              {...field}
                              type="text"
                              placeholder="system-ui, -apple-system, sans-serif"
                              className="flex-1 text-sm text-foreground bg-transparent border-none outline-none min-w-0"
                            />
                          </div>
                        )}
                      />
                    )}
                  </div>
                </section>

                {/* Logo */}
                <section className="px-2">
                  <h3 className="text-sm font-medium leading-none text-foreground mb-4">
                    Logo
                  </h3>
                  <Controller
                    name="logoUrl"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-[7px]">
                        {(field.value || logoFile) && (
                          <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
                            <img
                              src={
                                logoFile
                                  ? URL.createObjectURL(logoFile)
                                  : `/s3-files/${field.value}`
                              }
                              alt="Logo"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 flex items-center h-9 bg-white border border-base-input rounded-lg shadow-xs pl-3 pr-1.5 py-1 gap-2">
                          <span
                            className={`flex-1 min-w-0 text-sm truncate ${
                              logoFile || field.value
                                ? 'text-foreground'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {logoFile
                              ? logoFile.name
                              : field.value
                              ? field.value.split('/').pop()
                              : 'No file'}
                          </span>
                          <label className="bg-muted h-6 px-2 rounded-[3px] text-sm font-medium text-foreground shrink-0 cursor-pointer hover:bg-gray-500/20 flex items-center">
                            change
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) setLogoFile(file)
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  />
                </section>
              </div>
            )}

            {editorTab === 'content' && (
              <TranslationsEditor
                customerId={customerId!}
                previewLang={previewLang}
                onLangChange={setPreviewLang}
                activeTab={previewTab}
              />
            )}
          </form>
        </div>

        {/* Right column - Preview panel (fixed) */}
        <div className="flex-1 min-w-0 sticky top-0 self-start">
          <PreviewPanel
            theme={previewTheme}
            activeTab={previewTab}
            onTabChange={setPreviewTab}
            customerId={customerId!}
            previewLang={previewLang}
            previewMode={previewMode}
            onPreviewModeChange={setPreviewMode}
          />
        </div>
      </div>
    </div>
  )
}

// --- Preview Tabs ---

const PREVIEW_TABS = [
  { key: 'access', label: 'Access' },
  { key: 'select', label: 'Select Gift' },
  { key: 'success', label: 'Success' },
  { key: 'email', label: 'Email' },
] as const

const MOCK_ITEMS = [
  {
    id: 'mock-1',
    name: 'Premium Gift Card',
    description: 'A versatile gift card for any occasion.',
    picture: null,
    price: 25.0,
    type: 'intersolve',
  },
  {
    id: 'mock-2',
    name: 'Experience Voucher',
    description: 'Enjoy a unique experience of your choice.',
    picture: null,
    price: 50.0,
    type: 'physical',
  },
]

// reason: the address window needs something on file to show its confirm
// state; without it the preview would only ever render the empty form.
const MOCK_ADDRESS = {
  name: 'A. van Dijk',
  streetName: 'Kerkstraat',
  houseNumber: '12A',
  postcode: '1017 GH',
  city: 'Amsterdam',
  country: 'NL',
}

// --- Translation field config ---

type PreviewTabKey = 'access' | 'select' | 'success' | 'email'

const TRANSLATION_FIELDS: Record<
  PreviewTabKey,
  Array<{ key: string; label: string }>
> = {
  access: [
    { key: 'validate.title', label: 'Title' },
    { key: 'validate.subtitle', label: 'Subtitle' },
    { key: 'validate.cardNumber', label: 'Card number label' },
    { key: 'validate.pin', label: 'PIN label' },
    { key: 'validate.cardPlaceholder', label: 'Card placeholder' },
    { key: 'validate.pinPlaceholder', label: 'PIN placeholder' },
    { key: 'validate.continue', label: 'Continue button' },
    { key: 'validate.errors.invalid', label: 'Error: invalid' },
    { key: 'validate.errors.inactive', label: 'Error: inactive' },
    { key: 'validate.errors.redeemed', label: 'Error: redeemed' },
    { key: 'validate.errors.expired', label: 'Error: expired' },
    {
      key: 'validate.errors.addressRequired',
      label: 'Error: address required',
    },
    { key: 'validate.errors.emailRequired', label: 'Error: email required' },
    { key: 'validate.errors.unknown', label: 'Error: unknown' },
  ],
  select: [
    { key: 'select.title', label: 'Title' },
    { key: 'select.subtitle', label: 'Subtitle' },
    { key: 'select.noGifts', label: 'No gifts message' },
    { key: 'select.emailLabel', label: 'Email label' },
    { key: 'select.emailPlaceholder', label: 'Email placeholder' },
    { key: 'select.emailInvalid', label: 'Error: invalid email' },
    { key: 'select.chooseButton', label: 'Choose button' },
    { key: 'select.chosenButton', label: 'Chosen button' },
    { key: 'select.selectButton', label: 'Select button' },
    { key: 'select.addressTitle', label: 'Address window title' },
    { key: 'select.addressSubtitle', label: 'Address window subtitle' },
    { key: 'select.addressEmptyNotice', label: 'No address on file notice' },
    { key: 'select.addressSave', label: 'Save and claim button' },
    { key: 'select.addressNameLabel', label: 'Address: name label' },
    { key: 'select.addressStreetNameLabel', label: 'Address: street label' },
    {
      key: 'select.addressHouseNumberLabel',
      label: 'Address: house number label',
    },
    { key: 'select.addressPostcodeLabel', label: 'Address: postcode label' },
    { key: 'select.addressCityLabel', label: 'Address: city label' },
    { key: 'select.addressCountryLabel', label: 'Address: country label' },
    {
      key: 'select.addressFieldRequired',
      label: 'Address: required field message',
    },
  ],
  success: [
    { key: 'success.title', label: 'Title' },
    { key: 'success.body', label: 'Body text' },
    { key: 'success.errorTitle', label: 'Error title' },
    { key: 'success.tryAgain', label: 'Try again button' },
  ],
  email: [
    { key: 'email.subject', label: 'Subject' },
    { key: 'email.codeLabel', label: 'Code label' },
    { key: 'email.intro', label: 'Intro text' },
    { key: 'email.nameLabel', label: 'Name label' },
    { key: 'email.poweredBy', label: 'Powered by' },
  ],
}

const GLOBAL_FIELDS = [{ key: 'langSwitch', label: 'Language switch label' }]

// --- Helpers ---

function flattenTranslations(
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'object' && value !== null) {
      Object.assign(
        result,
        flattenTranslations(value as Record<string, unknown>, path),
      )
    } else {
      result[path] = String(value)
    }
  }
  return result
}

function unflattenTranslations(
  flat: Record<string, string>,
): PortalTranslations {
  const result: Record<string, Record<string, unknown> | string> = {}
  for (const [path, value] of Object.entries(flat)) {
    const parts = path.split('.')
    let current: Record<string, unknown> = result
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {}
      }
      current = current[parts[i]] as Record<string, unknown>
    }
    current[parts[parts.length - 1]] = value
  }
  return result as unknown as PortalTranslations
}

// --- Preview Panel ---

function PreviewPanel({
  theme,
  activeTab,
  onTabChange,
  customerId,
  previewLang,
  previewMode,
  onPreviewModeChange,
}: {
  theme: PortalTheme
  activeTab: PreviewTabKey
  onTabChange: (tab: PreviewTabKey) => void
  customerId: string
  previewLang: string
  previewMode: 'desktop' | 'mobile'
  onPreviewModeChange: (mode: 'desktop' | 'mobile') => void
}) {
  const customerClient = useCustomerClient()
  const [addressOpen, setAddressOpen] = useState(false)

  const { data: translationData } = useQuery({
    queryKey: ['portalTranslations', customerId],
    queryFn: async () => {
      const response = await customerClient.portalTranslations.query({
        params: { customerId },
        query: {},
      })
      return response.status === 200 ? response.body : null
    },
    enabled: !!customerId,
  })

  const translations = useMemo(() => {
    if (!translationData?.translations?.[previewLang]) {
      return getTranslations(previewLang === 'en' ? 'en' : 'nl')
    }
    const flat = flattenTranslations(
      translationData.translations[previewLang] as Record<string, unknown>,
    )
    return unflattenTranslations(flat)
  }, [translationData, previewLang])

  return (
    <div className="bg-base-muted border border-base-input rounded-lg shadow-xl overflow-hidden flex flex-col items-center px-3 h-[720px] relative">
      {/* Dot grid canvas - covers top 682px */}
      <div className="absolute top-0 left-0 right-0 pointer-events-none h-[682px] bg-[radial-gradient(circle,#A3A3A3_0.75px,transparent_0.75px)] bg-[length:30px_30px] bg-[position:8px_8px]" />

      {/* Title bar - plain text at top center */}
      <div className="relative z-10 px-4 py-3 mt-0">
        <span className="text-sm leading-none text-muted-foreground">
          Live Preview - {previewMode === 'desktop' ? 'Web' : 'Mobile'}
        </span>
      </div>

      {/* Preview content - outer white card matching Figma 25326:126506 */}
      {(() => {
        const cardW = previewMode === 'mobile' ? 375 : 562
        const cardH = 400
        const virtualW = previewMode === 'mobile' ? 480 : 1280
        const scale = cardW / virtualW
        const virtualH = cardH / scale
        return (
          <div
            className={cn(
              'relative z-10 my-auto shrink-0 bg-white rounded-lg shadow-xl overflow-hidden',
            )}
            style={{ width: `${cardW}px`, height: `${cardH}px` }}
          >
            <div
              className="portal-preview"
              style={{
                width: `${virtualW}px`,
                height: `${virtualH}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              {activeTab === 'access' && (
                <ValidatePage
                  theme={theme}
                  preview
                  translations={translations}
                  lang={previewLang}
                />
              )}
              {activeTab === 'select' && (
                <SelectPage
                  voucherCodeId="preview"
                  items={MOCK_ITEMS}
                  theme={theme}
                  preview
                  translations={translations}
                  lang={previewLang}
                  address={MOCK_ADDRESS}
                  previewAddressOpen={addressOpen}
                />
              )}
              {activeTab === 'success' && (
                <SuccessPage
                  theme={theme}
                  preview
                  translations={translations}
                  lang={previewLang}
                />
              )}
              {activeTab === 'email' && (
                <EmailPreview
                  theme={theme}
                  translations={translations}
                  preview
                />
              )}
            </div>
          </div>
        )
      })()}

      {activeTab === 'select' && (
        <div className="relative z-10 mt-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={addressOpen}
              onChange={(e) => setAddressOpen(e.target.checked)}
            />
            Show address window
          </label>
        </div>
      )}

      {/* Responsive switch - floating between card and tabs */}
      <div className="relative z-10 mt-auto mb-8">
        <div className="flex items-center gap-1.5 bg-white border border-base-input rounded-[12px] px-[7px] py-px shadow-xl">
          <button
            type="button"
            onClick={() => onPreviewModeChange('desktop')}
            className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${
              previewMode === 'desktop'
                ? 'bg-foreground text-white'
                : 'text-muted-foreground'
            }`}
          >
            <Monitor weight="regular" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onPreviewModeChange('mobile')}
            className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${
              previewMode === 'mobile'
                ? 'bg-foreground text-white'
                : 'text-muted-foreground'
            }`}
          >
            <DeviceMobileCamera weight="regular" className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bottom preview tabs */}
      <div className="relative z-10 mb-5">
        <div className="flex items-center bg-base-input p-[3px] rounded-[10px]">
          {PREVIEW_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className={`px-2 py-1 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// --- Translations Editor ---

function TranslationsEditor({
  customerId,
  previewLang,
  onLangChange,
  activeTab,
}: {
  customerId: string
  previewLang: string
  onLangChange: (lang: string) => void
  activeTab: PreviewTabKey
}) {
  const customerClient = useCustomerClient()
  const queryClient = useQueryClient()
  const { user } = useAuth0()

  const { data: translationData } = useQuery({
    queryKey: ['portalTranslations', customerId],
    queryFn: async () => {
      const response = await customerClient.portalTranslations.query({
        params: { customerId },
        query: {},
      })
      return response.status === 200 ? response.body : null
    },
    enabled: !!customerId,
  })

  const languages = translationData?.languages || ['nl', 'en']

  const [editableFlat, setEditableFlat] = useState<
    Record<string, Record<string, string>>
  >({})
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set())
  const apiFlatRef = useRef<Record<string, Record<string, string>>>({})

  useEffect(() => {
    if (!translationData?.translations) return
    const flat: Record<string, Record<string, string>> = {}
    for (const [lang, t] of Object.entries(translationData.translations)) {
      flat[lang] = flattenTranslations(t as Record<string, unknown>)
    }
    apiFlatRef.current = flat
    setEditableFlat(flat)
    setDirtyKeys(new Set())
  }, [translationData])

  const currentFlat = useMemo(
    () => editableFlat[previewLang] || {},
    [editableFlat, previewLang],
  )

  const handleFieldChange = useCallback(
    (key: string, value: string) => {
      setEditableFlat((prev) => ({
        ...prev,
        [previewLang]: { ...prev[previewLang], [key]: value },
      }))
      const dirtyKey = `${previewLang}:${key}`
      const apiValue = apiFlatRef.current[previewLang]?.[key]
      setDirtyKeys((prev) => {
        const next = new Set(prev)
        if (value === apiValue) {
          next.delete(dirtyKey)
        } else {
          next.add(dirtyKey)
        }
        return next
      })
    },
    [previewLang],
  )

  const activeLangDirtyCount = useMemo(() => {
    const prefix = `${previewLang}:`
    let count = 0
    for (const k of dirtyKeys) {
      if (k.startsWith(prefix)) count++
    }
    return count
  }, [dirtyKeys, previewLang])

  const [savingTranslations, setSavingTranslations] = useState(false)
  const [translationSaveMsg, setTranslationSaveMsg] = useState('')

  const saveTranslations = useCallback(async () => {
    if (activeLangDirtyCount === 0) return
    setSavingTranslations(true)
    setTranslationSaveMsg('')

    const prefix = `${previewLang}:`
    const entries: Array<{ key: string; value: string }> = []
    for (const dk of dirtyKeys) {
      if (dk.startsWith(prefix)) {
        const key = dk.slice(prefix.length)
        entries.push({ key, value: currentFlat[key] })
      }
    }

    try {
      const response = await customerClient.upsertPortalTranslations.mutate({
        params: { customerId },
        body: { lang: previewLang, translations: entries },
      })
      if (response.status !== 200) {
        throw new Error(
          (response.body as { message?: string })?.message || 'Failed to save',
        )
      }
      queryClient.invalidateQueries({
        queryKey: ['portalTranslations', customerId],
      })
      setTranslationSaveMsg('Translations saved')
      setTimeout(() => setTranslationSaveMsg(''), 3000)
    } catch (err: unknown) {
      setTranslationSaveMsg(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSavingTranslations(false)
    }
  }, [
    activeLangDirtyCount,
    previewLang,
    dirtyKeys,
    currentFlat,
    customerClient,
    customerId,
    queryClient,
  ])

  // Email test
  const [testEmail, setTestEmail] = useState('')
  const [sendStatus, setSendStatus] = useState('')

  useEffect(() => {
    if (user?.email && !testEmail) setTestEmail(user.email)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  const sendTestMutation = useMutation({
    mutationFn: async () => {
      const response = await customerClient.sendTestEmail.mutate({
        params: { customerId },
        body: { email: testEmail },
      })
      if (response.status !== 200) {
        throw new Error(
          (response.body as { message: string })?.message || 'Failed to send',
        )
      }
    },
    onSuccess: () => {
      setSendStatus('Sent!')
      setTimeout(() => setSendStatus(''), 3000)
    },
    onError: (err: Error) => {
      setSendStatus(err.message)
      setTimeout(() => setSendStatus(''), 5000)
    },
  })

  const tabFields = TRANSLATION_FIELDS[activeTab]

  return (
    <div className="space-y-4">
      {/* Language input — type a code to switch or create a new language */}
      <div className="flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Language:</label>
        <input
          type="text"
          value={previewLang}
          onChange={(e) =>
            onLangChange(e.target.value.toLowerCase().replace(/[^a-z]/g, ''))
          }
          placeholder="e.g. nl, en, de"
          className="border border-base-input rounded-lg px-2 py-1 text-sm h-8 w-20"
          maxLength={5}
        />
        {languages.length > 0 && (
          <div className="flex gap-1">
            {languages.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => onLangChange(lang)}
                className={`text-xs px-2 py-1 rounded-md border ${
                  previewLang === lang
                    ? 'bg-base-muted border-base-input font-medium'
                    : 'border-transparent hover:bg-base-muted/50'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Translation fields */}
      <div className="space-y-3">
        {tabFields.map((field) => {
          const dirtyKey = `${previewLang}:${field.key}`
          const isDirty = dirtyKeys.has(dirtyKey)
          return (
            <div key={field.key}>
              <label className="text-xs font-medium text-muted-foreground block mb-0.5">
                {field.label}
                {isDirty && <span className="ml-1 text-blue-500">*</span>}
              </label>
              <input
                type="text"
                value={currentFlat[field.key] ?? ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className={`w-full border rounded-lg px-2 py-1.5 text-sm ${
                  isDirty
                    ? 'border-blue-300 bg-blue-50/30'
                    : 'border-base-input'
                }`}
              />
            </div>
          )
        })}

        {GLOBAL_FIELDS.map((field) => {
          const dirtyKey = `${previewLang}:${field.key}`
          const isDirty = dirtyKeys.has(dirtyKey)
          return (
            <div key={field.key}>
              <label className="text-xs font-medium text-muted-foreground block mb-0.5">
                {field.label}
                {isDirty && <span className="ml-1 text-blue-500">*</span>}
              </label>
              <input
                type="text"
                value={currentFlat[field.key] ?? ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className={`w-full border rounded-lg px-2 py-1.5 text-sm ${
                  isDirty
                    ? 'border-blue-300 bg-blue-50/30'
                    : 'border-base-input'
                }`}
              />
            </div>
          )
        })}
      </div>

      {/* Save translations */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="small"
          label={
            savingTranslations
              ? 'Saving...'
              : `Save Translations${
                  activeLangDirtyCount > 0 ? ` (${activeLangDirtyCount})` : ''
                }`
          }
          disabled={savingTranslations || activeLangDirtyCount === 0}
          onClick={saveTranslations}
        />
        {translationSaveMsg && (
          <span
            className={`text-sm ${
              translationSaveMsg === 'Translations saved'
                ? 'text-green-600'
                : 'text-red-600'
            }`}
          >
            {translationSaveMsg}
          </span>
        )}
      </div>

      {/* Email test (shown when email tab active) */}
      {activeTab === 'email' && (
        <div className="flex items-center gap-2 pt-2 border-t border-base-input">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="recipient@example.com"
            className="border border-base-input rounded-lg px-3 py-1.5 text-sm w-64"
          />
          <Button
            type="button"
            size="small"
            label={
              sendTestMutation.isPending ? 'Sending...' : 'Send Test Email'
            }
            disabled={sendTestMutation.isPending || !testEmail}
            onClick={() => sendTestMutation.mutate()}
          />
          {sendStatus && (
            <span
              className={`text-sm ${
                sendStatus === 'Sent!' ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {sendStatus}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
